import { defaultHeaders } from '@/api'

export type ConsentState = {
  necessary: true
  analytics: boolean
  decidedAt: string
  version: 1
}

const STORAGE_KEY = 'benepicks.cookie-consent'
const EXPIRY_MS = 365 * 24 * 60 * 60 * 1000

type Subscriber = (state: ConsentState | null) => void
const subscribers = new Set<Subscriber>()

let listenerInstalled = false
const installStorageListener = () => {
  if (listenerInstalled || typeof window === 'undefined') return
  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return
    const next = getConsent()
    subscribers.forEach((cb) => cb(next))
  })
  listenerInstalled = true
}

const isExpired = (state: ConsentState): boolean => {
  const decidedAt = new Date(state.decidedAt).getTime()
  if (Number.isNaN(decidedAt)) return true
  return Date.now() - decidedAt >= EXPIRY_MS
}

export const getConsent = (): ConsentState | null => {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const state = JSON.parse(raw) as ConsentState
    if (state.version !== 1 || isExpired(state)) {
      window.localStorage.removeItem(STORAGE_KEY)
      return null
    }
    return state
  } catch {
    return null
  }
}

export const setConsent = (analytics: boolean): ConsentState => {
  const state: ConsentState = {
    necessary: true,
    analytics,
    decidedAt: new Date().toISOString(),
    version: 1,
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch (error) {
    console.error('[CookieConsent] Failed to persist consent:', error)
  }
  subscribers.forEach((cb) => cb(state))
  return state
}

export const clearConsent = (): void => {
  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch (error) {
    console.error('[CookieConsent] Failed to clear consent:', error)
  }
  subscribers.forEach((cb) => cb(null))
}

export const subscribeConsent = (cb: Subscriber): (() => void) => {
  installStorageListener()
  subscribers.add(cb)
  return () => {
    subscribers.delete(cb)
  }
}

// Records the consent timestamp on the authenticated user. The endpoint is
// public + idempotent: anonymous calls return 204 no-op, authenticated calls
// write the row first-write-wins. localStorage is the runtime source of
// truth; the DB column is the audit trail for actual customers.
//
// Pass { force: true } from the banner click path so re-consent after the
// 12-month expiry overwrites the server timestamp. The bridge sync hook
// (anon-consents-then-signs-up) uses the default first-write-wins behavior.
export const syncConsentToServer = async (
  options: { force?: boolean } = {}
): Promise<void> => {
  if (typeof window === 'undefined') return
  if (!getConsent()) return
  try {
    await fetch('/cookie_consent', {
      method: 'POST',
      headers: defaultHeaders,
      body: JSON.stringify(options.force ? { force: true } : {}),
    })
  } catch {
    // Network failures are non-fatal — the CYA record is best-effort.
  }
}
